import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import {truncateText} from '../../../shared/utils/truncate-text';
import glamorous from 'glamorous';
import {ASH, WHITE, TARMAC, FOCUS_BLUE, GUNSMOKE, SHADOW} from '../../../shared/style/colors';
import {BASE_TEXT, WEIGHT} from '../../../shared/style/typography';
import {grid} from '../../../shared/utils/grid';
import Text from '../../../shared/library/typography/text';
import Upvotes from '../../../shared/library/icons/upvotes-icon.svg';
import Views from '../../../shared/library/icons/views-icon.svg';
import Decision from '../../../shared/library/icons/mydecisions-icon.svg';
import {formatCount} from '../../../shared/utils/format';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {NavigationContext} from '../../../shared/enhancers/router-enhancer';
import {useSendAnalyticsEvent} from '../../../shared/enhancers/analytics-enhancer';
import PrivatePublicIndicator from '../../../shared/library/private-public-indicator';
import {
  POST_TYPE_DESCRIPTIONS,
  POST_TYPE_ICONS,
  POST_TYPE_FREEFORM
} from '../../../shared/library/cards/post/constants';
import {taggableStack} from '../../../shared/library/composer/utils';
import {renderContext, renderRelationsip} from '../../../shared/library/cards/post/index.jsx';
import AutoGeneratedIcon from '../../../shared/library/icons/auto_generated.svg';
import sanitizeData from './../../../shared/utils/sanitize-data';

const MAX_CHARS_AUTHOR = 40;
const CONTEXT_MARGIN = 7;

const PrivPubContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  zIndex: 1
});

const Context = glamorous.div({
  width: '100%',
  marginTop: 15,
  display: 'flex',
  alignItems: 'center',
  ...BASE_TEXT,
  flexWrap: 'wrap',
  ' > svg:first-of-type': {
    marginRight: CONTEXT_MARGIN
  },
  ' > a': {
    textDecoration: 'none',
    marginRight: CONTEXT_MARGIN
  },
  ' > a:last-of-type': {
    textDecoration: 'none',
    marginRight: 0
  },
  ' > *': {
    marginBottom: 13
  },
  ' > a > span:last-of-type': {
    fontSize: 14
  }
});

const Noun = glamorous.div({
  ...BASE_TEXT,
  fontSize: 14,
  fontWeight: WEIGHT.BOLD,
  color: SHADOW,
  marginRight: CONTEXT_MARGIN
});

const Container = glamorous.div({
  position: 'relative',
  boxSizing: 'border-box',
  height: 318,
  padding: grid(3),
  border: `1px solid ${ASH}`,
  background: WHITE,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  ':hover': {
    boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.31)'
  }
});

const Header = glamorous.div();

const HeaderText = glamorous.div();

const Content = glamorous.div({
  flex: 1,
  overflowY: 'hidden',
  position: 'relative',
  wordBreak: 'break-word'
});

const Overlay = glamorous.div({
  position: 'absolute',
  bottom: -2,
  left: 0,
  width: '100%',
  height: '50%'
});

const ImageWrapper = glamorous.div({
  width: 50,
  height: 50,
  borderWidth: 1,
  float: 'left',
  borderRadius: '50%',
  overflow: 'hidden',
  marginRight: 14
});

const Image = glamorous.img({
  width: 50
});

const Author = glamorous.p({
  ...BASE_TEXT,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '0.3px',
  margin: 0
});

const Role = glamorous.p({
  ...BASE_TEXT,
  fontSize: 13,
  letterSpacing: '0.2px',
  color: TARMAC,
  margin: 0,
  minHeight: 10
});

const DecisionContent = glamorous.div({
  display: 'inline',
  fontSize: 13,
  ' p': {
    ...BASE_TEXT,
    fontSize: 13,
    lineHeight: 5 / 3,
    letterSpacing: 0.2
  },
  ' p:last-child': {
    display: 'inline'
  },
  ' ul, ol': {
    ' li': {
      ...BASE_TEXT,
      fontSize: 13,
      lineHeight: 5 / 3,
      letterSpacing: 0.2
    }
  },
  ' a, a:visited': {
    textDecoration: 'underline',
    cursor: 'pointer',
    color: FOCUS_BLUE,
    ':hover': {
      textDecoration: 'underline'
    }
  },
  ' pre': {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    background: TARMAC,
    color: WHITE,
    margin: '20px 0',
    fontFamily: 'monospace',
    fontSize: 13,
    padding: 10,
    borderRadius: 0
  },
  ' code': {
    borderRadius: 0,
    background: TARMAC,
    color: WHITE,
    fontSize: 13
  },
  ' .tool, .topic': {
    padding: '1px 3px 2px 3px',
    borderRadius: 2,
    background: ASH
  }
});

const StyledDecision = glamorous(Decision)({
  border: 'none',
  color: GUNSMOKE,
  height: 22
});

const Footer = glamorous.div({
  display: 'flex',
  paddingTop: grid(2),
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: `1px solid ${ASH}`
});

const FooterContainer = glamorous.div({
  display: 'flex',
  alignItems: 'center'
});

const StyledUpvotes = glamorous(Upvotes)({
  marginRight: grid(0.5),
  '& path': {
    fill: GUNSMOKE
  }
});

const StyledViews = glamorous(Views)({
  marginRight: grid(0.5),
  '& path': {
    fill: GUNSMOKE
  }
});

const StyledText = glamorous(Text)({
  color: GUNSMOKE,
  marginRight: 10
});

const Status = glamorous.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-around',
  paddingTop: grid(4)
});

const AutoGeneratedIconStyled = glamorous(AutoGeneratedIcon)({
  marginLeft: 9,
  marginBottom: -1
});

const DecisionCardLite = ({decision}) => {
  const sendAnalyticsEvent = useSendAnalyticsEvent();
  const navigate = useContext(NavigationContext);
  const {
    htmlContent,
    private: isPrivate,
    upvotesCount,
    user,
    viewCount,
    services,
    company,
    decisionType,
    fromTools,
    subjectTools,
    toTools,
    stack,
    showAutoGeneratedTag
  } = decision;
  let postType, postStructure;
  if (!decisionType) {
    postType = POST_TYPE_FREEFORM;
    postStructure = {subjectTools: services};
  } else {
    postType = decisionType;
    postStructure = {fromTools, subjectTools, toTools};
  }
  const taggedStack = stack ? taggableStack(stack) : null;

  if (!user) {
    return (
      <Container>
        <Status>
          <Circular size={LARGE} />
          <Text>Updating...</Text>
        </Status>
      </Container>
    );
  }

  return (
    <Container
      onClick={() => {
        sendAnalyticsEvent('click_decisionCardLite');
        navigate(`/${user.username}/decisions/${decision.id}`);
      }}
    >
      {isPrivate && (
        <PrivPubContainer>
          <PrivatePublicIndicator typeIndicator="BlueRoundLarge" />
        </PrivPubContainer>
      )}
      <Content>
        <Overlay />
        <Header>
          <ImageWrapper>
            <Image src={user.imageUrl} />
          </ImageWrapper>
          <HeaderText>
            <Author>
              {user.displayName && truncateText(user.displayName, MAX_CHARS_AUTHOR)}
              {showAutoGeneratedTag && <AutoGeneratedIconStyled />}
            </Author>
            <Role>
              {user.title && user.title}{' '}
              {user.companyName && user.title && `at ${user.companyName}`}
            </Role>
          </HeaderText>
        </Header>

        <RenderingTopics
          postType={postType}
          postStructure={postStructure}
          company={company}
          taggedStack={taggedStack}
          isPrivate={isPrivate}
        />

        <DecisionContent
          dangerouslySetInnerHTML={{
            __html: sanitizeData(htmlContent)
          }}
        />
      </Content>
      <Footer>
        <FooterContainer>
          <StyledUpvotes /> <StyledText>{formatCount(upvotesCount)}</StyledText>
          <StyledViews /> <StyledText>{formatCount(viewCount)}</StyledText>
        </FooterContainer>
        <StyledDecision />
      </Footer>
    </Container>
  );
};

const RenderingTopics = ({postType, postStructure, company, taggedStack, isPrivate}) => {
  return (
    <Context>
      {POST_TYPE_ICONS[postType]}
      <Noun>{POST_TYPE_DESCRIPTIONS[postType]}</Noun>
      {renderRelationsip(postType, postStructure, () => {}, isPrivate, false, false)}
      {(company || taggedStack) && renderContext({company, stack: taggedStack})}
    </Context>
  );
};

RenderingTopics.propTypes = {
  postType: PropTypes.string,
  postStructure: PropTypes.object,
  company: PropTypes.object,
  taggedStack: PropTypes.object,
  isPrivate: PropTypes.bool
};

export default DecisionCardLite;

DecisionCardLite.propTypes = {
  decision: PropTypes.object
};

DecisionCardLite.defaultProps = {
  tools: [],
  topics: []
};
